import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ClipboardList, FileText, Calendar, Activity,
  Users, Stethoscope, CheckCircle, ArrowRight,
  Eye, Lock, Play
} from "lucide-react";

const DEMO_EMAIL = "demo@aurorageriatrica.com";
const DEMO_PASSWORD = "Demo1234!";

const FEATURES = [
  { icon: ClipboardList, title: "Historia clínica completa", desc: "Antecedentes, examen físico y datos generales" },
  { icon: Stethoscope, title: "Consulta geriátrica estructurada", desc: "Historia clínica, examen y plan en un solo flujo" },
  { icon: Activity, title: "Alertas clínicas automáticas", desc: "Caídas, fragilidad, nutrición, polifarmacia" },
  { icon: FileText, title: "Informes PDF automáticos", desc: "Informe clínico, familiar y plan terapéutico" },
  { icon: Calendar, title: "Seguimiento longitudinal", desc: "Historial completo de consultas del paciente" },
  { icon: Users, title: "Gestión de pacientes", desc: "6 pacientes de ejemplo listos para explorar" },
];

export default function DemoHome() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleEnterDemo = async () => {
    setLoading(true);
    setError("");
    try {
      await login(DEMO_EMAIL, DEMO_PASSWORD);
      navigate("/dashboard");
    } catch (e) {
      setError("No se pudo acceder al demo. Intente de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50">

      {/* Header */}
      <div className="border-b border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <img
            src="/assets/logo/logo-wordmark.png"
            alt="Aurora Geriátrica"
            className="h-7 w-auto object-contain"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <Link to="/login">
            <Button variant="outline" size="sm" className="border-slate-200 text-slate-600">
              Iniciar sesión
            </Button>
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-12">

        {/* Hero */}
        <div className="mb-12 text-center">
          <Badge className="mb-4 bg-sky-50 text-sky-700 border-sky-200 px-3 py-1">
            <Play className="mr-1.5 h-3 w-3" />
            Demo en vivo
          </Badge>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Explora Aurora Geriátrica
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
            Navega libremente con una cuenta demo real — 6 pacientes completos, consultas, informes PDF y alertas clínicas listas para explorar.
          </p>
        </div>

        {/* Features grid */}
        <div className="mb-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 border border-sky-100">
                <Icon className="h-4 w-4 text-sky-600" />
              </div>
              <p className="text-sm font-semibold text-slate-800">{title}</p>
              <p className="mt-1 text-xs text-slate-500">{desc}</p>
            </div>
          ))}
        </div>

        {/* Demo access card */}
        <div className="mx-auto max-w-md rounded-2xl border border-sky-200 bg-white p-6 shadow-lg">
          <div className="mb-5 text-center">
            <Eye className="mx-auto mb-2 h-8 w-8 text-sky-600" />
            <h2 className="text-lg font-bold text-slate-900">Acceso al demo</h2>
            <p className="mt-1 text-sm text-slate-500">Entra con un clic — no necesitas crear cuenta</p>
          </div>

          <div className="mb-5 space-y-2 rounded-xl bg-slate-50 border border-slate-200 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Email</span>
              <span className="font-medium text-slate-800">{DEMO_EMAIL}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Contraseña</span>
              <span className="font-medium text-slate-800">{DEMO_PASSWORD}</span>
            </div>
          </div>

          {error && (
            <p className="mb-3 text-center text-sm text-red-600">{error}</p>
          )}

          <Button
            className="w-full h-11 rounded-xl bg-sky-600 text-white font-semibold hover:bg-sky-700"
            onClick={handleEnterDemo}
            disabled={loading}
          >
            {loading ? "Entrando..." : (
              <>
                Entrar al demo
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>

          <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <Lock className="h-3 w-3" />
            Entorno de demostración — datos ficticios
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <p className="text-sm text-slate-500 mb-3">¿Listo para usar Aurora en su práctica?</p>
          <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
            <Button asChild className="rounded-xl bg-slate-900 text-white hover:bg-slate-800 px-6">
              <Link to="/register">
                Comenzar prueba gratis — 14 días
                <CheckCircle className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-xl border-slate-200 text-slate-600">
              <Link to="/login">Ya tengo cuenta</Link>
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
}
